import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { useFeatureEntitlements } from "@frontegg/react";
import { Alert, Button, FormControlLabel, Switch } from "@mui/material";
import { Accordion, Drawer, Select, TextField } from "@outpave/ds";
import AttachmentsComponent, {
  AttachmentRenderType,
} from "components/common/attachments/AttachmentComponent";
import {
  LocalImageAsset,
  useAttachmentAndAssetHandling,
} from "hooks/useAttachmentAndAssetHandling";
import { ATTACHMENT_PARENT_TYPE } from "enums/AttachmentParentType";
import { AttachmentDrawerType } from "enums/AttachmentDrawerType";
import { Columns, Stack } from "components/layout/reusableLayouts";
import paymentLinkValidation from "validations/paymentLink";
import {
  PaymentLinkAmountMode,
  PaymentLinkSuccessType,
  PaymentLinkType,
} from "enums/PaymentLink";
import {
  emptyPaymentLinkForm,
  PaymentLink,
  PaymentLinkFormValues,
  toPaymentLinkForm,
  usePaymentLinkData,
} from "hooks/usePaymentLinkData";
import { intervalOptions } from "utils/billingInterval";
import { previewUrl } from "utils/paymentLinkUrl";

interface CreateEditPaymentLinkDrawerProps {
  open: boolean;
  paymentLink?: PaymentLink | null;
  defaultLinkType?: PaymentLinkType;
  onClose: (refresh?: boolean, createdId?: string) => void;
  /** Fires once the drawer has slid out. */
  onAfterClose?: () => void;
}

const FIELDS_BY_SECTION: Record<string, (keyof PaymentLinkFormValues)[]> = {
  afterPayment: ["successRedirectUrl"],
  expiry: ["expiresAt"],
};

const CreateEditPaymentLinkDrawer: React.FC<
  CreateEditPaymentLinkDrawerProps
> = ({ open, paymentLink, defaultLinkType, onClose, onAfterClose }) => {
  const { t } = useTranslation();
  const { savePaymentLink, loading } = usePaymentLinkData();

  const { isEntitled: featureCashDiscount } = useFeatureEntitlements(
    "feature_cash_discount",
  );
  const { isEntitled: featureFeeDonation } = useFeatureEntitlements(
    "feature_fee_donation",
  );

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [pendingFiles, setPendingFiles] = useState<LocalImageAsset[]>([]);

  const { handleAddAttachments } = useAttachmentAndAssetHandling({});

  const formik = useFormik<PaymentLinkFormValues>({
    initialValues: paymentLink
      ? toPaymentLinkForm(paymentLink)
      : emptyPaymentLinkForm(defaultLinkType),
    enableReinitialize: true,
    validationSchema: paymentLinkValidation,
    onSubmit: async (values) => {
      const saved = await savePaymentLink(values, paymentLink?.id);
      if (!saved) return;

      // Attachments need the new link's id before upload.
      if (pendingFiles.length) {
        await handleAddAttachments(pendingFiles, {
          parentId: saved.id,
          parentType: ATTACHMENT_PARENT_TYPE.PAYMENT_LINK,
        });
        setPendingFiles([]);
      }

      onClose(true, paymentLink ? undefined : saved.id);
    },
  });

  const isSubscription =
    formik.values.linkType === PaymentLinkType.SUBSCRIPTION;
  // Recurring mandates require a fixed amount.
  const isFixed =
    isSubscription || formik.values.amountMode === PaymentLinkAmountMode.FIXED;
  const offersPayerPricing = featureCashDiscount || featureFeeDonation;

  const errorFor = (field: keyof PaymentLinkFormValues) =>
    formik.touched[field] && formik.errors[field]
      ? String(formik.errors[field])
      : undefined;

  const sectionHasError = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(FIELDS_BY_SECTION).map(([section, fields]) => [
          section,
          fields.some((field) => formik.touched[field] && formik.errors[field]),
        ]),
      ),
    [formik.touched, formik.errors],
  );

  // Expand sections containing validation errors so the payer can see them.
  const sectionProps = (section: string) => ({
    expanded: !!openSections[section] || !!sectionHasError[section],
    onChange: (_: unknown, expanded: boolean) =>
      setOpenSections((previous) => ({ ...previous, [section]: expanded })),
  });

  // Save is still running while the link's files upload after it.
  const saving = loading || formik.isSubmitting;

  return (
    <Drawer
      open={open}
      onClose={() => onClose()}
      onAfterClose={onAfterClose}
      loading={saving}
      title={paymentLink ? t("Edit Payment Link") : t("New Payment Link")}
      footer={
        <>
          <Button variant="default-outlined" onClick={() => onClose()}>
            {t("Cancel")}
          </Button>
          {paymentLink?.slug && (
            <Button
              variant="default-outlined"
              onClick={() =>
                window.open(
                  previewUrl(paymentLink),
                  "_blank",
                  "noopener,noreferrer",
                )
              }
            >
              {t("Preview")}
            </Button>
          )}
          <Button disabled={saving} onClick={() => formik.handleSubmit()}>
            {paymentLink ? t("Save") : t("Create Link")}
          </Button>
        </>
      }
    >
      <Stack>
        {isSubscription && !paymentLink && (
          <Alert severity="info" role="status">
            {t("paymentLink.RecurringNotice")}
          </Alert>
        )}

        <TextField
          fullWidth
          required
          name="name"
          label={t("Name")}
          helperText={errorFor("name") || t("paymentLink.NameHelper")}
          error={!!errorFor("name")}
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />

        <TextField
          fullWidth
          multiline
          minRows={2}
          name="description"
          label={t("Description")}
          value={formik.values.description}
          onChange={formik.handleChange}
        />

        <Columns count={2}>
          <Select
            fullWidth
            clearable={false}
            label={t("Charge type")}
            value={formik.values.linkType}
            onChange={(value) => formik.setFieldValue("linkType", value)}
            disabled={!!paymentLink}
            helperText={paymentLink ? t("paymentLink.ChargeTypeLocked") : ""}
            options={[
              { id: PaymentLinkType.ONE_TIME, label: t("One time") },
              {
                id: PaymentLinkType.SUBSCRIPTION,
                label: t("Recurring"),
              },
            ]}
          />

          {isSubscription ? (
            <Select
              fullWidth
              clearable={false}
              label={t("Billing frequency")}
              value={formik.values.interval}
              onChange={(value) => formik.setFieldValue("interval", value)}
              options={intervalOptions().map(({ id, label }) => ({
                id,
                label: t(label),
              }))}
            />
          ) : (
            <Select
              fullWidth
              clearable={false}
              label={t("Amount")}
              value={formik.values.amountMode}
              onChange={(value) => formik.setFieldValue("amountMode", value)}
              options={[
                {
                  id: PaymentLinkAmountMode.FIXED,
                  label: t("Fixed amount"),
                },
                {
                  id: PaymentLinkAmountMode.PAYER_CHOOSES,
                  label: t("Payer chooses"),
                },
              ]}
            />
          )}
        </Columns>

        {isSubscription && (
          <TextField
            fullWidth
            type="number"
            name="maxPayments"
            label={t("Stop after this many payments")}
            error={!!errorFor("maxPayments")}
            helperText={
              errorFor("maxPayments") || t("paymentLink.MaxPaymentsHelper")
            }
            value={formik.values.maxPayments ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        )}

        {isSubscription && (
          <TextField
            fullWidth
            type="date"
            name="endsAt"
            label={t("Stop billing on this date")}
            error={!!errorFor("endsAt")}
            helperText={errorFor("endsAt") || t("paymentLink.EndsAtHelper")}
            value={formik.values.endsAt ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        )}

        {isFixed ? (
          <TextField
            fullWidth
            required
            type="number"
            name="amount"
            label={isSubscription ? t("Amount per cycle") : t("Price")}
            error={!!errorFor("amount")}
            helperText={errorFor("amount")}
            value={formik.values.amount ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        ) : (
          <Columns count={2}>
            <TextField
              fullWidth
              type="number"
              name="minAmount"
              label={t("Minimum")}
              error={!!errorFor("minAmount")}
              helperText={errorFor("minAmount") || t("No minimum")}
              value={formik.values.minAmount ?? ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <TextField
              fullWidth
              type="number"
              name="maxAmount"
              label={t("Maximum")}
              error={!!errorFor("maxAmount")}
              helperText={errorFor("maxAmount") || t("No maximum")}
              value={formik.values.maxAmount ?? ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Columns>
        )}

        {!isFixed && (
          <TextField
            fullWidth
            name="presetAmountsText"
            label={t("Suggested amounts")}
            placeholder="25, 50, 100"
            error={!!errorFor("presetAmountsText")}
            helperText={
              errorFor("presetAmountsText") ||
              t("paymentLink.PresetAmountsHelper")
            }
            value={formik.values.presetAmountsText}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        )}

        <Accordion title={t("Attachments")} {...sectionProps("attachments")}>
          <AttachmentsComponent
            renderType={AttachmentRenderType.ACCORDION}
            drawerType={AttachmentDrawerType.ATTACHMENT}
            // Local until Save uploads them, so an edit cannot upload a file this list never shows.
            parentDetails={{
              parentId: "",
              parentType: ATTACHMENT_PARENT_TYPE.PAYMENT_LINK,
            }}
            dragAndDropHint={t("paymentLink.AttachmentsHint")}
            localAssets={pendingFiles}
            onAddAssets={(assets) =>
              setPendingFiles((previous) => [...previous, ...assets])
            }
            onRemoveAssets={(assetId) =>
              setPendingFiles((previous) =>
                previous.filter((asset) => asset.id !== assetId),
              )
            }
          />
        </Accordion>

        {offersPayerPricing && (
          <Accordion
            title={t("Fees and discounts")}
            {...sectionProps("pricing")}
          >
            <Stack>
              {featureFeeDonation && (
                <FormControlLabel
                  control={
                    <Switch
                      name="feeCoverEnabled"
                      checked={formik.values.feeCoverEnabled}
                      onChange={formik.handleChange}
                    />
                  }
                  label={t("paymentLink.OfferFeeDonation")}
                />
              )}

              {featureCashDiscount && (
                <FormControlLabel
                  control={
                    <Switch
                      name="cashDiscountEnabled"
                      checked={formik.values.cashDiscountEnabled}
                      onChange={formik.handleChange}
                    />
                  }
                  label={t("paymentLink.OfferCashDiscount")}
                />
              )}
            </Stack>
          </Accordion>
        )}

        {!isSubscription && (
          <Accordion title={t("Tips")} {...sectionProps("tips")}>
            <Stack>
              <FormControlLabel
                control={
                  <Switch
                    name="tipEnabled"
                    checked={formik.values.tipEnabled}
                    onChange={formik.handleChange}
                  />
                }
                label={t("paymentLink.OfferTip")}
              />

              {formik.values.tipEnabled && (
                <TextField
                  fullWidth
                  name="tipPresetPercentages"
                  label={t("Suggested percentages")}
                  placeholder="10, 15, 20"
                  value={formik.values.tipPresetPercentages}
                  onChange={formik.handleChange}
                  error={!!errorFor("tipPresetPercentages")}
                  helperText={
                    errorFor("tipPresetPercentages") ||
                    t("paymentLink.TipPresetsHelper")
                  }
                />
              )}
            </Stack>
          </Accordion>
        )}

        <Accordion title={t("Expire after")} {...sectionProps("expiry")}>
          <TextField
            fullWidth
            type="date"
            name="expiresAt"
            label={t("Date")}
            error={!!errorFor("expiresAt")}
            helperText={errorFor("expiresAt") || t("paymentLink.ExpiresHelper")}
            value={toDateInputValue(formik.values.expiresAt)}
            onChange={(event) =>
              formik.setFieldValue(
                "expiresAt",
                toEndOfChosenDay(event.target.value),
              )
            }
          />
        </Accordion>

        <Accordion title={t("After payment")} {...sectionProps("afterPayment")}>
          <Stack>
            <Select
              fullWidth
              clearable={false}
              label={t("What the payer sees")}
              value={formik.values.successType}
              onChange={(value) => formik.setFieldValue("successType", value)}
              options={[
                {
                  id: PaymentLinkSuccessType.MESSAGE,
                  label: t("Show a message"),
                },
                {
                  id: PaymentLinkSuccessType.REDIRECT,
                  label: t("Redirect to a page"),
                },
              ]}
            />

            {formik.values.successType === PaymentLinkSuccessType.MESSAGE ? (
              <TextField
                fullWidth
                multiline
                minRows={2}
                name="successMessage"
                label={t("Message")}
                value={formik.values.successMessage}
                onChange={formik.handleChange}
              />
            ) : (
              <TextField
                fullWidth
                required
                name="successRedirectUrl"
                label={t("Destination URL")}
                error={!!errorFor("successRedirectUrl")}
                helperText={errorFor("successRedirectUrl")}
                value={formik.values.successRedirectUrl}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            )}
          </Stack>
        </Accordion>
      </Stack>
    </Drawer>
  );
};

/* Expire at the end of the merchant's chosen local day. */
function toEndOfChosenDay(value: string): string | null {
  if (!value) return null;

  const [year, month, day] = value.split("-").map(Number);

  return new Date(year, month - 1, day, 23, 59, 59, 999).toISOString();
}

function toDateInputValue(iso: string | null): string {
  if (!iso) return "";

  const date = new Date(iso);
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${date.getFullYear()}-${month}-${day}`;
}

export default CreateEditPaymentLinkDrawer;
